import React, { useState } from 'react';
import axios from 'axios';
import PublicPageWrapper from '../../components/PublicPageWrapper';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { CheckCircle, ArrowLeft, ArrowRight, FileText, User, BookOpen, Heart } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const STEPS = ['Student Info', 'Division & Grade', 'Guardian Info', 'Health & History', 'Review & Submit'];
const divisions = [
  { name: 'Pre-School', grades: ['ECD A', 'ECD B'] },
  { name: 'Primary', grades: ['Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5', 'Grade 6', 'Grade 7'] },
  { name: 'High School', grades: ['Form 1', 'Form 2', 'Form 3', 'Form 4', 'Form 5'] },
];

export default function AdmissionsPage() {
  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [refNumber, setRefNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    child_first_name: '', child_last_name: '', child_dob: '', child_gender: '',
    division: '', grade: '',
    guardian_name: '', guardian_email: '', guardian_phone: '', guardian_relationship: 'Parent',
    previous_school: '', medical_info: '', address: '',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const grades = divisions.find(d => d.name === form.division)?.grades || [];
  const canNext = () => {
    if (step === 0) return form.child_first_name && form.child_last_name && form.child_dob && form.child_gender;
    if (step === 1) return form.division && form.grade;
    if (step === 2) return form.guardian_name && form.guardian_email && form.guardian_phone;
    return true;
  };
  const handleSubmit = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/admissions/apply`, form);
      setRefNumber(data.reference_number); setSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Submission failed'); }
    finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <PublicPageWrapper>
        <div className="max-w-xl mx-auto text-center py-10" data-testid="admissions-page">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5"><CheckCircle className="w-8 h-8 text-emerald-600" /></div>
          <h1 className="text-2xl font-bold text-[#1B2A4A] mb-3">Application Submitted!</h1>
          <p className="text-slate-500 text-sm mb-5">Please save your reference number:</p>
          <div className="bg-[#1B2A4A] rounded-2xl p-6 inline-block mb-6"><p className="text-[10px] text-[#E8A830] uppercase font-bold tracking-wider mb-1">Reference Number</p><p className="text-2xl font-mono font-black text-white" data-testid="reference-number">{refNumber}</p></div>
          <div className="flex justify-center gap-3">
            <a href="/status-checker"><Button className="rounded-full bg-[#1B2A4A] text-xs font-bold h-9 px-5">Check Status</Button></a>
            <a href="/"><Button variant="outline" className="rounded-full text-xs font-bold h-9 px-5">Back to Home</Button></a>
          </div>
        </div>
      </PublicPageWrapper>
    );
  }

  return (
    <PublicPageWrapper title="Apply to Tadam Academy" subtitle="Complete all steps to submit your enrolment application. Zimbabwe curriculum - ECD through A-Level.">
      <div className="max-w-2xl mx-auto" data-testid="admissions-page">
        <div className="flex items-center justify-center mb-8 gap-1 flex-wrap">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-bold ${i === step ? 'bg-[#1B2A4A] text-white' : i < step ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'}`}>
                <span className="w-4 h-4 rounded-full bg-white/20 flex items-center justify-center text-[9px]">{i < step ? '✓' : i + 1}</span>
                <span className="hidden sm:inline">{s}</span>
              </div>
              {i < STEPS.length - 1 && <div className={`w-6 h-0.5 ${i < step ? 'bg-emerald-400' : 'bg-slate-200'}`} />}
            </React.Fragment>
          ))}
        </div>
        <Card className="border-slate-100"><CardContent className="p-6">
          {step === 0 && (
            <div className="space-y-4" data-testid="admissions-step-1">
              <div className="flex items-center gap-2 mb-3"><User className="w-5 h-5 text-[#E8A830]" /><h2 className="text-lg font-bold text-[#1B2A4A]">Student Information</h2></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>First Name *</Label><Input value={form.child_first_name} onChange={e => set('child_first_name', e.target.value)} data-testid="child-first-name" /></div>
                <div><Label>Last Name *</Label><Input value={form.child_last_name} onChange={e => set('child_last_name', e.target.value)} data-testid="child-last-name" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Date of Birth *</Label><Input type="date" value={form.child_dob} onChange={e => set('child_dob', e.target.value)} /></div>
                <div><Label>Gender *</Label><Select value={form.child_gender} onValueChange={v => set('child_gender', v)}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent><SelectItem value="Male">Male</SelectItem><SelectItem value="Female">Female</SelectItem></SelectContent></Select></div>
              </div>
              <div><Label>Address</Label><Input value={form.address} onChange={e => set('address', e.target.value)} /></div>
            </div>
          )}
          {step === 1 && (
            <div className="space-y-4" data-testid="admissions-step-2">
              <div className="flex items-center gap-2 mb-3"><BookOpen className="w-5 h-5 text-[#E8A830]" /><h2 className="text-lg font-bold text-[#1B2A4A]">Division & Grade</h2></div>
              <div><Label>Division *</Label><Select value={form.division} onValueChange={v => { set('division', v); set('grade', ''); }}><SelectTrigger><SelectValue placeholder="Select Division" /></SelectTrigger><SelectContent>{divisions.map(d => <SelectItem key={d.name} value={d.name}>{d.name}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Grade / Form *</Label><Select value={form.grade} onValueChange={v => set('grade', v)}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{grades.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent></Select></div>
            </div>
          )}
          {step === 2 && (
            <div className="space-y-4" data-testid="admissions-step-3">
              <div className="flex items-center gap-2 mb-3"><User className="w-5 h-5 text-[#E8A830]" /><h2 className="text-lg font-bold text-[#1B2A4A]">Guardian Information</h2></div>
              <div><Label>Full Name *</Label><Input value={form.guardian_name} onChange={e => set('guardian_name', e.target.value)} data-testid="guardian-name" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Email *</Label><Input type="email" value={form.guardian_email} onChange={e => set('guardian_email', e.target.value)} data-testid="guardian-email" /></div>
                <div><Label>Phone *</Label><Input value={form.guardian_phone} onChange={e => set('guardian_phone', e.target.value)} data-testid="guardian-phone" /></div>
              </div>
              <div><Label>Relationship</Label><Select value={form.guardian_relationship} onValueChange={v => set('guardian_relationship', v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Parent">Parent</SelectItem><SelectItem value="Guardian">Guardian</SelectItem><SelectItem value="Grandparent">Grandparent</SelectItem></SelectContent></Select></div>
            </div>
          )}
          {step === 3 && (
            <div className="space-y-4" data-testid="admissions-step-4">
              <div className="flex items-center gap-2 mb-3"><Heart className="w-5 h-5 text-[#E8A830]" /><h2 className="text-lg font-bold text-[#1B2A4A]">Health & Prior Schooling</h2></div>
              <div><Label>Previous School</Label><Input value={form.previous_school} onChange={e => set('previous_school', e.target.value)} placeholder="Name of previous school (if any)" /></div>
              <div><Label>Medical Information</Label><Textarea value={form.medical_info} onChange={e => set('medical_info', e.target.value)} placeholder="Allergies, conditions, medications..." rows={3} /></div>
            </div>
          )}
          {step === 4 && (
            <div data-testid="admissions-step-5">
              <div className="flex items-center gap-2 mb-3"><FileText className="w-5 h-5 text-[#E8A830]" /><h2 className="text-lg font-bold text-[#1B2A4A]">Review Application</h2></div>
              <div className="space-y-3 text-sm">
                <div className="bg-slate-50 rounded-lg p-4"><p className="font-bold text-slate-600 mb-1">Student</p><p>{form.child_first_name} {form.child_last_name} | {form.child_gender} | DOB: {form.child_dob}</p><p>Applying for: {form.division} - {form.grade}</p></div>
                <div className="bg-slate-50 rounded-lg p-4"><p className="font-bold text-slate-600 mb-1">Guardian</p><p>{form.guardian_name} ({form.guardian_relationship})</p><p>{form.guardian_email} | {form.guardian_phone}</p></div>
              </div>
            </div>
          )}
          <div className="flex justify-between mt-6">
            <Button variant="outline" onClick={() => setStep(s => s - 1)} disabled={step === 0} className="rounded-full h-9 px-5 text-xs font-bold"><ArrowLeft className="w-3.5 h-3.5 mr-1" /> Previous</Button>
            {step < STEPS.length - 1 ? (
              <Button onClick={() => setStep(s => s + 1)} disabled={!canNext()} className="rounded-full bg-[#1B2A4A] h-9 px-5 text-xs font-bold" data-testid={`admissions-step-${step + 1}-next`}>Next <ArrowRight className="w-3.5 h-3.5 ml-1" /></Button>
            ) : (
              <Button onClick={handleSubmit} disabled={loading} className="rounded-full bg-[#E8A830] hover:bg-[#D4982A] text-white h-9 px-5 text-xs font-bold" data-testid="admissions-submit-btn">{loading ? 'Submitting...' : 'Submit Application'}</Button>
            )}
          </div>
        </CardContent></Card>
      </div>
    </PublicPageWrapper>
  );
}
